import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ProjectsPage.css';
import Testimonials from '../components/Testimonials';
import Footer from '../components/Footer';
import { FaWhatsapp, FaTimes, FaExternalLinkAlt, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// Assets
import vedHospitalImage from '../assets/project-ved-hospital.png';
import myGymImage from '../assets/mygym.png';
import jalaramSawmillImage from '../assets/project-jalaram.png';
import windsonPackImage from '../assets/project-windson.png';
import shreeBloodCenterImage from '../assets/project-shree-blood.png';
import nextForensicsImage from '../assets/nextforensics.png';
import magicByteLabImage from '../assets/magicbyte.png';
import clinicManagementImage from '../assets/project-clinic-ms.png';

const ProjectsPage = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = next, -1 = prev
  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);

  const whatsappNumber = '+917600236927';
  const whatsappMessage = 'Hi! I am interested in your deploy-x architecture.';
  const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  React.useEffect(() => {
    const tooltipTimer = setTimeout(() => {
      setShowWhatsAppTooltip(true);
      setTimeout(() => setShowWhatsAppTooltip(false), 5000);
    }, 3000);
    return () => clearTimeout(tooltipTimer);
  }, []);

  const projects = [
    {
      id: 1, title: "Clinic OS Management",
      description: "Desktop app for clinical data analysis, patient charting, and treatment systems.",
      fullDescription: "Developed a comprehensive clinic management system for handling doctor data, patient records, treatment plans, and data analysis with robust reporting tools.",
      image: clinicManagementImage, category: "desktop",
      technologies: ["Electron", "React", "SQLite"],
      link: "https://clinic-info.vercel.app/", year: "2023"
    },
    {
      id: 2, title: "Next Forensics",
      description: "An AI-powered forensic tool that converts crime scene images into VR-driven 3D environments with evidence marking.",
      fullDescription: "Next Forensics is a cutting-edge AI-powered forensic investigation tool that transforms 2D crime scene photographs into fully immersive VR-driven 3D environments. Investigators can walk through reconstructed crime scenes, identify and mark evidence in 3D space, and collaborate remotely — revolutionizing digital forensic analysis and courtroom presentations.",
      image: nextForensicsImage, category: "ai",
      technologies: ["React", "Three.js", "WebXR", "AI/ML"],
      link: "https://vr-fawn-one.vercel.app/", year: "2024"
    },
    {
      id: 3, title: "MagicByte Lab",
      description: "A powerful tool that converts magic bytes to Java code, hex code, decimal code and more.",
      fullDescription: "MagicByte Lab is a developer utility tool that allows users to convert magic bytes (file signatures) into various formats including Java code, hexadecimal, decimal, and ASCII representations. Built for security researchers and developers who work with file format analysis.",
      image: magicByteLabImage, category: "ai",
      technologies: ["HTML5", "CSS3", "JavaScript"],
      link: "https://magicbyte-lab.vercel.app/", year: "2024"
    },
    {
      id: 4, title: "MyGym",
      description: "A web platform that guides users on performing exercises correctly with video demos, workout plans, and progress tracking.",
      fullDescription: "MyGym is an interactive fitness web platform designed to help users learn proper exercise form through detailed video demonstrations, create personalized workout plans, track their fitness progress, and explore exercises categorized by muscle groups. Built to make professional-grade fitness guidance accessible to everyone.",
      image: myGymImage, category: "web",
      technologies: ["React", "CSS3", "JavaScript"],
      link: "https://my-gym-app-sooty.vercel.app/", year: "2024"
    },
    {
      id: 5, title: "Shree Blood Bank",
      description: "A fast, scalable platform managing blood donations, tracking inventory, and organizing donor info.",
      fullDescription: "Developed a full-featured blood bank management system with donor registration, blood inventory tracking, appointment scheduling, and real-time availability. Improved operational efficiency by 60%.",
      image: shreeBloodCenterImage, category: "web",
      technologies: ["React", "Firebase", "Express"],
      link: "https://www.shreebloodcenter.in/", year: "2024"
    },
    {
      id: 6, title: "Ved Hospital Network",
      description: "A comprehensive portal for a multispecialty hospital with doctor profiles and appointment booking.",
      fullDescription: "A modern healthcare portal for Ved Hospital to streamline patient interactions. This includes online appointment booking, dynamic department and doctor listings, and a patient testimonials section.",
      image: vedHospitalImage, category: "web",
      technologies: ["React", "Express", "Node.js", "MongoDB"],
      link: "http://vedhospital.co.in/", year: "2023"
    },
    {
      id: 7, title: "Jalaram Sawmill E-Catalog",
      description: "A digital catalog and logistics platform for an elite timber sawmill business.",
      fullDescription: "A digital catalog and inventory viewing platform allowing customers to browse and inquire about timber products online. Features include live product stock tracking and an immersive gallery.",
      image: jalaramSawmillImage, category: "web",
      technologies: ["HTML5", "JavaScript", "PHP"],
      link: "https://jalaramsawmill.in/", year: "2022"
    },
    {
      id: 8, title: "Windson Packaging Hub",
      description: "A professional corporate website for a leading food packaging industry.",
      fullDescription: "Designed an elite corporate profile and product showcase for Windson Packaging, establishing a powerful online presence and high conversion rates for their B2B operations.",
      image: windsonPackImage, category: "web",
      technologies: ["WordPress", "Elementor", "PHP"],
      link: "https://windsonpack.com/", year: "2023"
    }
  ];

  const categories = [
    { id: 'all', label: 'All Fields' },
    { id: 'web', label: 'Web Platform' },
    { id: 'ai', label: 'AI / ML' },
    { id: 'desktop', label: 'Software' }
  ];

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(p => p.category === filter);

  // Reset index when filter changes
  useEffect(() => {
    setCurrentIndex(0);
    setDirection(1);
  }, [filter]);

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % filteredProjects.length);
  }, [filteredProjects.length]);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + filteredProjects.length) % filteredProjects.length);
  }, [filteredProjects.length]);

  // Auto-play: cycle every 5 seconds
  useEffect(() => {
    if (filteredProjects.length <= 1) return;
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext, filteredProjects.length]);

  const currentProject = filteredProjects[currentIndex];

  // Slide animation variants
  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] }
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.4, ease: [0.25, 1, 0.5, 1] }
    })
  };

  return (
    <>
      <main className="projects-vault-wrapper">
        <div className="projects-page-elite">
          {/* Background */}
          <div className="elite-bg-animation">
            <div className="elite-ambient-glow center-glow"></div>
            <div className="elite-ambient-glow top-right-glow"></div>
          </div>

          {/* Hero */}
          <motion.section
            className="elite-projects-hero"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="elite-hero-badge">
              <span className="pulse-dot"></span> DEPLOY-X CASE STUDIES
            </div>
            <h1 className="elite-hero-title">
              Architecting Digital <br />
              <span className="neon-text">Legacies.</span>
            </h1>
            <p className="elite-hero-subtitle">
              We don't just build software. We engineer high-performance systems and stunning user experiences designed to scale and dominate.
            </p>
          </motion.section>

          {/* Filters */}
          <motion.div
            className="elite-filter-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="elite-filter-container">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`elite-filter-btn ${filter === cat.id ? 'active' : ''}`}
                  onClick={() => setFilter(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Single Showcase Area */}
          <div className="showcase-single-container">
            {/* Navigation Arrows */}
            {filteredProjects.length > 1 && (
              <>
                <button className="showcase-nav-btn showcase-nav-prev" onClick={goPrev}>
                  <FaChevronLeft />
                </button>
                <button className="showcase-nav-btn showcase-nav-next" onClick={goNext}>
                  <FaChevronRight />
                </button>
              </>
            )}

            {/* Card */}
            <div className="showcase-viewport">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentProject?.id}
                  className="showcase-hero-card"
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  onClick={() => setSelectedProject(currentProject)}
                >
                  {/* Browser frame */}
                  <div className="browser-frame-bar">
                    <span className="frame-dot"></span>
                    <span className="frame-dot"></span>
                    <span className="frame-dot"></span>
                  </div>

                  {/* Image */}
                  <div className="showcase-hero-image">
                    <img src={currentProject?.image} alt={currentProject?.title} />
                    <div className="showcase-hero-gradient"></div>
                  </div>

                  {/* Content */}
                  <div className="showcase-hero-content">
                    <h2 className="showcase-hero-title">{currentProject?.title}</h2>
                    <div className="showcase-hero-meta">
                      <div className="showcase-meta-left">
                        <span className="showcase-meta-label">TECHNOLOGIES</span>
                        <div className="showcase-tech-pills">
                          {currentProject?.technologies.map((tech, i) => (
                            <span key={i} className="showcase-pill">{tech}</span>
                          ))}
                        </div>
                      </div>
                      <div className="showcase-meta-center">
                        <span className="showcase-meta-label">SUMMARY</span>
                        <p className="showcase-summary">{currentProject?.description}</p>
                      </div>
                      <div className="showcase-meta-right">
                        <span className="showcase-meta-label">YEAR</span>
                        <span className="showcase-year">{currentProject?.year}</span>
                      </div>
                    </div>
                  </div>

                  {/* Hover CTA */}
                  <div className="showcase-hero-cta">
                    <span className="showcase-cta-btn">
                      <FaArrowRight /> View Project
                    </span>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Progress Dots */}
            {filteredProjects.length > 1 && (
              <div className="showcase-dots">
                {filteredProjects.map((_, i) => (
                  <button
                    key={i}
                    className={`showcase-dot ${i === currentIndex ? 'active' : ''}`}
                    onClick={() => { setDirection(i > currentIndex ? 1 : -1); setCurrentIndex(i); }}
                  />
                ))}
              </div>
            )}

            {/* Counter */}
            <div className="showcase-counter">
              <span className="showcase-counter-current">{String(currentIndex + 1).padStart(2, '0')}</span>
              <span className="showcase-counter-sep">/</span>
              <span className="showcase-counter-total">{String(filteredProjects.length).padStart(2, '0')}</span>
            </div>
          </div>
        </div>

        {/* Modal */}
        <AnimatePresence>
          {selectedProject && (
            <motion.div
              className="elite-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            >
              <motion.div
                className="elite-modal-content"
                initial={{ scale: 0.92, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.92, opacity: 0, transition: { duration: 0.3 } }}
                transition={{ type: "spring", damping: 30, stiffness: 250 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button className="elite-modal-close" onClick={() => setSelectedProject(null)}>
                  <FaTimes />
                </button>

                <div className="modal-hero-image">
                  <img src={selectedProject.image} alt={selectedProject.title} />
                  <div className="modal-hero-gradient"></div>
                </div>

                <div className="elite-modal-body">
                  <div className="elite-modal-meta">
                    <span className="elite-modal-tag">
                      {categories.find(c => c.id === selectedProject.category)?.label}
                    </span>
                    <span className="elite-modal-year">{selectedProject.year}</span>
                  </div>

                  <h2 className="elite-modal-title">{selectedProject.title}</h2>
                  <p className="elite-modal-desc">{selectedProject.fullDescription}</p>

                  <div className="elite-modal-tech-block">
                    <h4>Architecture Stack</h4>
                    <div className="elite-modal-tech-grid">
                      {selectedProject.technologies.map((tech, i) => (
                        <span key={i} className="elite-tech-wire">{tech}</span>
                      ))}
                    </div>
                  </div>

                  {selectedProject.link !== "#" && (
                    <a href={selectedProject.link} target="_blank" rel="noreferrer" className="elite-deploy-btn">
                      <FaExternalLinkAlt /> LAUNCH SYSTEM
                    </a>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <Testimonials />
        <Footer />
      </main>

      {/* WhatsApp */}
      <div className="whatsapp-float-container">
        <AnimatePresence>
          {showWhatsAppTooltip && (
            <motion.div className="whatsapp-tooltip"
              initial={{ opacity: 0, x: 20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            >
              <button className="tooltip-close" onClick={() => setShowWhatsAppTooltip(false)}><FaTimes /></button>
              <p className="tooltip-text"><strong>Start a build?</strong><br />Chat directly with DEPLOY-X.</p>
            </motion.div>
          )}
        </AnimatePresence>
        <motion.a href={whatsappURL} target="_blank" rel="noopener noreferrer" className="whatsapp-button"
          initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 1 }}
          whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          onMouseEnter={() => setShowWhatsAppTooltip(true)}
          onMouseLeave={() => setShowWhatsAppTooltip(false)}
        >
          <FaWhatsapp className="whatsapp-icon" />
          <span className="pulse-ring"></span>
          <span className="pulse-ring pulse-ring-delay"></span>
        </motion.a>
      </div>
    </>
  );
};

export default ProjectsPage;